import { computed, type Ref } from 'vue';

import type { BrandColor } from '@/types/company';

type BrandColorRole = 'primary' | 'accent';

/** primary and accent views over a list of brand colours; edits the list in place and drops a role when it is cleared */
export function useBrandColors(colors: Ref<BrandColor[]>) {
  /* --------------------------------- METHODS ---------------------------------- */
  function upsertColor(role: BrandColorRole, value: string) {
    const existing = colors.value.find((color) => color.role === role);

    if (!value) {
      if (existing) colors.value.splice(colors.value.indexOf(existing), 1);
      return;
    }

    if (existing) {
      existing.value = value;
    } else {
      colors.value.push({ value, role });
    }
  }

  /* --------------------------------- COMPUTED --------------------------------- */
  const primaryColor = computed({
    get: () => colors.value.find((color) => color.role === 'primary')?.value ?? '',
    set: (value: string) => upsertColor('primary', value),
  });

  const accentColor = computed({
    get: () => colors.value.find((color) => color.role === 'accent')?.value ?? '',
    set: (value: string) => upsertColor('accent', value),
  });

  return { primaryColor, accentColor };
}
